import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Upload, Plus } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { ExcelIcon } from "@/shared/icons/ExcelIcon";

interface EntityListHeaderShellProps {
  searchPlaceholder: string;
  sampleSheetPath: string;
  bulkUploadPath: string;
  addPath: string;
  addLabel: string;
  filterControl: React.ReactNode;
  footerContent?: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

export function EntityListHeaderShell({
  searchPlaceholder,
  sampleSheetPath,
  bulkUploadPath,
  addPath,
  addLabel,
  filterControl,
  footerContent,
  searchValue,
  onSearchChange,
}: EntityListHeaderShellProps) {
  const { t } = useTranslation("common");

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="relative w-[320px]">
              <Input
                placeholder={searchPlaceholder}
                value={searchValue ?? ""}
                onChange={(e) => onSearchChange?.(e.target.value)}
                className="bg-white pr-10 pl-3 border border-[#E5E7EB] focus-visible:border-[#3B82F6] rounded-lg focus-visible:ring-[#3B82F6] focus-visible:ring-1 w-full h-9 text-[#374151] placeholder:text-[#9CA3AF] text-sm caret-[#3B82F6]"
              />
              <Search
                className="top-1/2 right-3 absolute w-5 h-5 text-[#9CA3AF] -translate-y-1/2 pointer-events-none"
                aria-hidden
              />
            </div>
            {filterControl}
          </div>

          <div className="flex items-center gap-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={sampleSheetPath}
                  className="inline-flex justify-center items-center bg-white hover:bg-[#F9FAFB] p-0 border-[#E5E7EB] border-0 rounded-lg w-9 h-9 transition-colors"
                  aria-label={t("filter.sampleSheet")}
                >
                  <ExcelIcon />
                </Link>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="bg-[#F3F4F6] text-[#1F2937]"
              >
                {t("filter.sampleSheet")}
              </TooltipContent>
            </Tooltip>

            <Button
              variant="outline"
              className="bg-white hover:bg-[#F9FAFB] px-4 border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-lg h-9 text-[#374151]"
              asChild
            >
              <Link to={bulkUploadPath}>
                <Upload className="w-4 h-4 text-[#374151]" />
                <span>{t("actions.bulkUpload")}</span>
              </Link>
            </Button>

            <Button
              type="button"
              variant="outline"
              className="bg-white hover:bg-[#EFF6FF] px-4 border border-[#3B82F6] hover:border-[#2563EB] rounded-lg h-9 text-[#3B82F6]"
              asChild
            >
              <Link to={addPath}>
                <Plus className="w-4 h-4 text-[#3B82F6]" />
                <span>{addLabel}</span>
              </Link>
            </Button>
          </div>
        </div>
        {footerContent}
      </div>
    </TooltipProvider>
  );
}
